/**
 * Videos that opt out of motion under prefers-reduced-motion.
 *
 * A <video> with a poster and no resolvable source paints its poster, so
 * dropping the sources is all it takes to land on a still frame — no extra
 * markup, and the bytes are never fetched. Opt-in per block via
 * data-felix-video-reduced-motion="poster"; unflagged videos are untouched.
 */
export function initReducedMotionVideos() {
	if (!window.matchMedia || !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
		return
	}

	document.querySelectorAll('video[data-felix-video-reduced-motion="poster"]').forEach((video) => {
		video.removeAttribute('autoplay')
		video.querySelectorAll('source').forEach((source) => source.remove())
		video.pause()
		video.load()
		video.dataset.felixVideoLoaded = 'true'
	})
}

/**
 * Defer <video> source loading until near viewport.
 *
 * Videos rendered with preload="none" + data-felix-video-lazy="true"
 * keep their <source> URLs in data-src attributes. This module hydrates
 * those sources when the video approaches the viewport.
 */
export function initLazyVideos() {
	const lazyVideos = Array.from(document.querySelectorAll('video[data-felix-video-lazy="true"]'))
	if (!lazyVideos.length) {
		return
	}

	const loadVideo = (video) => {
		if (!video || video.dataset.felixVideoLoaded === 'true') {
			return
		}

		const sources = video.querySelectorAll('source[data-src]')
		sources.forEach((source) => {
			const src = source.getAttribute('data-src')
			if (!src) {
				return
			}
			source.setAttribute('src', src)
			source.removeAttribute('data-src')
		})

		video.dataset.felixVideoLoaded = 'true'
		video.load()

		if (video.autoplay) {
			video.play().catch(() => {})
		}
	}

	if (!('IntersectionObserver' in window)) {
		lazyVideos.forEach(loadVideo)
		return
	}

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) {
					return
				}
				loadVideo(entry.target)
				observer.unobserve(entry.target)
			})
		},
		{
			root: null,
			rootMargin: '300px 0px',
			threshold: 0.01,
		}
	)

	lazyVideos.forEach((video) => observer.observe(video))
}
